<template>
    <div v-if="room" class="checkout-hotel-room">
        <div class="checkout-hotel-room-header" padding="m">
            <div class="checkout-hotel-room-title">
                <strong>{{ roomTitle }}</strong>
            </div>
            <div>{{ roomGuests }}</div>
        </div>

        <div class="checkout-hotel-room-name">
            <TruncateText v-if="roomName" :text="roomName" :rows="1" />
        </div>

        <div class="checkout-hotel-room-fields">
            <div class="checkout-hotel-room-fields-genders">
                <div class="checkout-hotel-room-fields-genders-label">
                    {{ t({ key: 'shared.gender' }) }}
                </div>
                <RadioField
                    v-model="room.Pax.Gender"
                    :name="genderFieldName"
                    :input="{ items: genderItems, inline: true }" />
            </div>

            <TextboxField
                v-model="room.Pax.FirstName"
                :name="firstNameFieldName"
                :input="{
                    label: t({ key: 'checkout.first_name_placeholder_label' }),
                    capitalize: true,
                    autocomplete: 'given-name',
                }" />
            <TextboxField
                v-model="room.Pax.LastName"
                :name="lastNameFieldName"
                :input="{
                    label: t({ key: 'checkout.last_name_placeholder_label' }),
                    capitalize: true,
                    autocomplete: 'family-name',
                }" />
            <DateInputField
                v-model="room.Pax.DateOfBirth"
                v-if="showDateOfBirth"
                :name="dateOfBirthFieldName"
                :input="{ label: t({ key: 'shared.date_of_birth' }) }" />
        </div>

        <SpecialRequest :pax="room.Pax" />
        <Divider v-if="showDivider" color="lightGrey" thickness="1" topMargin="xxl" />
    </div>
</template>

<script lang="ts">
    import { t } from '@shared/helpers/translation';
    import { computed, defineComponent, PropType } from 'vue';
    import { HotelProduct, MandatoryFieldId, Room } from '@/api/Cart/cart.interfaces';
    import { useScreenSize } from '@shared/helpers/viewport';
    import { isMandatory } from '@shared/helpers/product';
    import { capitalizeWords } from '@shared/utils/string';
    import { getRoomGuests } from '@/helpers/guests';
    import { getGendersByProductAndPax } from '@/helpers/checkout';
    import TextboxField from '@ui/components/FormV2/Fields/Textbox/TextboxField.vue';
    import RadioField from '@ui/components/FormV2/Fields/Radio/RadioField.vue';
    import DateInputField from '@ui/components/FormV2/Fields/DateInput/DateInputField.vue';
    import TruncateText from '@ui/components/TruncateText/TruncateText.vue';
    import SpecialRequest from '@/components/checkoutPageV3/Shared/SpecialRequest/SpecialRequest.vue';
    import Divider from '@ui/components/Divider/Divider.vue';

    export default defineComponent({
        name: 'Room',
        methods: { t },
        components: {
            TruncateText,
            DateInputField,
            RadioField,
            TextboxField,
            SpecialRequest,
            Divider,
        },
        props: {
            room: { type: Object as PropType<Room>, required: true },
            product: { type: Object as PropType<HotelProduct>, required: true },
            index: { type: Number, required: true },
            multiple: { type: Boolean, default: false },
        },
        setup(props) {
            const { isTablet } = useScreenSize();
            const showDivider = computed<boolean>(() => {
                return props.index < props.product.Rooms.length - 1;
            });

            const genderItems = computed(() =>
                getGendersByProductAndPax(props.product, props.room.Pax)
            );

            const showDateOfBirth = computed(() =>
                isMandatory(props.room.Pax.MandatoryFields, MandatoryFieldId.DATE_OF_BIRTH)
            );

            const roomTitle = computed(() => {
                const suffix = props.multiple ? ` ${props.index + 1}` : '';
                const title = `${t({ key: 'shared.room', suffix })}`;
                return capitalizeWords(title);
            });

            const roomName = computed(() => capitalizeWords(props.room?.Description));

            const roomGuests = computed(() => getRoomGuests(props.room));

            const genderFieldName = computed(() => formatName('gender'));
            const firstNameFieldName = computed(() => formatName('firstName'));
            const lastNameFieldName = computed(() => formatName('lastName'));
            const dateOfBirthFieldName = computed(() => formatName('dateOfBirth'));
            const specialRequestFieldName = computed(() => formatName('specialRequest'));

            function formatName(name: string) {
                return `room${props.index}.${name}`;
            }

            return {
                roomTitle,
                roomGuests,
                roomName,
                isTablet,
                genderItems,
                showDateOfBirth,
                genderFieldName,
                firstNameFieldName,
                lastNameFieldName,
                dateOfBirthFieldName,
                specialRequestFieldName,
                showDivider,
            };
        },
    });
</script>

<style lang="scss" scoped>
    @forward './Room';
</style>
